import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import {
  Plus,
  Search,
  MapPin,
  Trash2,
  Image as ImageIcon,
  ImagePlus,
  LayoutGrid,
  Map as MapIcon,
  Shuffle,
} from "lucide-react";
import USMap from "../../components/portfolio/USMap";
import { getLinkMeta } from "../../components/portfolio/linkMeta";
import { PACKAGE_SCOPES, canCuratePortfolio } from "../../components/portfolio/scopes";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PortfolioGallery() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = canCuratePortfolio(user);
  const canMerge = user?.role === "COO" || user?.role === "Admin";

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("All");
  const [viewMode, setViewMode] = useState("grid");
  const [scopeFilter, setScopeFilter] = useState("all");
  const [mergeState, setMergeState] = useState({ open: false, loading: false, plan: null });
  const [canDelete, setCanDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // The server decides who may delete — the button only mirrors it.
  useEffect(() => {
    apiClient.get("/portfolio/scopes")
      .then(res => setCanDelete(Boolean(res.data?.can_delete_projects)))
      .catch(() => setCanDelete(false));
  }, []);

  // Search runs as you type, so hold off a beat instead of firing a request per
  // keystroke. The server matches title, client, city, state and tags.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const fetchProjects = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (debouncedSearch) params.append("search", debouncedSearch);

      const response = await apiClient.get(`/portfolio/projects?${params.toString()}`);
      setProjects(response.data);
    } catch (error) {
      toast.error("Failed to load portfolio");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedSearch]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  // Category pills, computed client-side like the old VEXOS Links filter —
  // counts stay accurate against the currently loaded (status/search-filtered) set.
  const categories = useMemo(
    () => [...new Set(projects.map(p => p.project_type).filter(Boolean))].sort(),
    [projects]
  );

  const filteredProjects = useMemo(() => {
    let list = typeFilter === "All" ? projects : projects.filter(p => p.project_type === typeFilter);
    // "Show me every project we animated" — the deliverable filter sales asks for.
    if (scopeFilter !== "all") list = list.filter(p => (p.scope_counts?.[scopeFilter] || 0) > 0);
    return list;
  }, [projects, typeFilter, scopeFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProjects();
  };

  // Cards take the thumbnail — a full gallery of full-res covers is megabytes
  // per row, and these render at ~400px.
  const getImageUrl = (project) => {
    const mediaId = project.hero_image?.id || project.hero_image_id;
    return mediaId ? `${API}/portfolio/media/${mediaId}/download?size=thumb` : null;
  };

  const getVexosLink = (project) => {
    const m = project.links?.find(m => m.media_type === "vexos_link");
    return m?.url || null;
  };

  const getDropboxLink = (project) => {
    const m = project.links?.find(m => m.media_type === "dropbox_link" || m.media_type === "folder_link");
    return m?.url || null;
  };

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/portfolio/projects/${deleteTarget.id}`);
      toast.success(`${deleteTarget.title?.trim() || "Project"} deleted`);
      setDeleteTarget(null);
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete that project");
    } finally {
      setDeleting(false);
    }
  }

  async function runMergeDryRun() {
    setMergeState({ open: true, loading: true, plan: null });
    try {
      const res = await apiClient.post("/portfolio/merge-vexos-links?dry_run=true");
      setMergeState({ open: true, loading: false, plan: res.data });
    } catch (err) {
      toast.error("Failed to preview the VEXOS Links merge");
      setMergeState({ open: false, loading: false, plan: null });
    }
  }

  async function confirmMerge() {
    setMergeState(s => ({ ...s, loading: true }));
    try {
      const res = await apiClient.post("/portfolio/merge-vexos-links?dry_run=false");
      toast.success(`Merged ${res.data.merged_count} project${res.data.merged_count === 1 ? "" : "s"}, added ${res.data.created_count} new`);
      setMergeState({ open: false, loading: false, plan: null });
      fetchProjects();
    } catch (err) {
      toast.error("Merge failed");
      setMergeState(s => ({ ...s, loading: false }));
    }
  }

  return (
    <div className="space-y-8" data-testid="portfolio-gallery">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light tracking-tight" style={{ fontFamily: 'var(--font-sans)' }}>
            Portfolio
          </h1>
          <p className="text-muted-foreground mt-1">
            Showcase of our completed projects
          </p>
        </div>
        <div className="flex gap-2">
          {canMerge && (
            <Button onClick={runMergeDryRun} variant="outline" className="rounded-full" data-testid="merge-vexos-links-btn">
              <Shuffle className="w-4 h-4 mr-2" />
              Merge VEXOS Links
            </Button>
          )}
          {isAdmin && (
            <Button onClick={() => navigate("/portfolio/add")} className="rounded-full" data-testid="add-project-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Project
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <form onSubmit={handleSearch} className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by project, client, city or state…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-full"
              data-testid="search-input"
            />
          </div>
        </form>

        <div className="flex gap-2 items-center">
          {isAdmin && (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px] rounded-full" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Published">Published</SelectItem>
                <SelectItem value="Draft">Draft</SelectItem>
                <SelectItem value="Archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          )}

          <Select value={scopeFilter} onValueChange={setScopeFilter}>
            <SelectTrigger className="w-[160px] rounded-full" data-testid="scope-filter">
              <SelectValue placeholder="Deliverable" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any deliverable</SelectItem>
              {PACKAGE_SCOPES.map(s => (
                <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex rounded-full border overflow-hidden h-9 text-sm shrink-0">
            <button
              className={`px-3 flex items-center gap-1.5 transition-colors ${viewMode === "grid" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              onClick={() => setViewMode("grid")}
              data-testid="view-mode-grid"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              Grid
            </button>
            <button
              className={`px-3 flex items-center gap-1.5 border-l transition-colors ${viewMode === "map" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
              onClick={() => setViewMode("map")}
              data-testid="view-mode-map"
            >
              <MapIcon className="w-3.5 h-3.5" />
              Map
            </button>
          </div>
        </div>
      </div>

      {/* Category pills */}
      {categories.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap -mt-4">
          {["All", ...categories].map(cat => (
            <button
              key={cat}
              onClick={() => setTypeFilter(cat)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors border ${
                typeFilter === cat
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border hover:bg-muted text-muted-foreground"
              }`}
              data-testid={`category-pill-${cat}`}
            >
              {cat}
              {cat !== "All" && (
                <span className="ml-1.5 text-xs opacity-70">
                  ({projects.filter(p => p.project_type === cat).length})
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="aspect-[4/3] bg-muted rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="text-center py-20">
          <ImageIcon className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
          <h3 className="text-lg font-medium text-muted-foreground">No projects yet</h3>
          <p className="text-sm text-muted-foreground/70 mt-1">
            {isAdmin ? "Add your first portfolio project to get started" : "Check back later for new projects"}
          </p>
          {isAdmin && (
            <Button onClick={() => navigate("/portfolio/add")} className="mt-4 rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              Add First Project
            </Button>
          )}
        </div>
      ) : viewMode === "map" ? (
        <USMap
          items={filteredProjects.map(p => ({
            id: p.id, lat: p.lat, lng: p.lng, location: p.location, title: p.title,
            project_type: p.project_type, coverImageUrl: getImageUrl(p),
          }))}
          onPinClick={(item) => navigate(`/portfolio/${item.id}`)}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProjects.map(project => (
            <ProjectCard
              key={project.id}
              project={project}
              isAdmin={isAdmin}
              imageUrl={getImageUrl(project)}
              vexosLink={getVexosLink(project)}
              dropboxLink={getDropboxLink(project)}
              onClick={() => navigate(`/portfolio/${project.id}`)}
              canDelete={canDelete}
              onDelete={() => setDeleteTarget(project)}
              onCoverUploaded={fetchProjects}
            />
          ))}
        </div>
      )}

      <MergeVexosLinksDialog
        state={mergeState}
        onClose={() => setMergeState({ open: false, loading: false, plan: null })}
        onConfirm={confirmMerge}
      />

      <DeleteProjectDialog
        project={deleteTarget}
        deleting={deleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

function MergeVexosLinksDialog({ state, onClose, onConfirm }) {
  const { open, loading, plan } = state;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Merge VEXOS Links into Portfolio</DialogTitle>
          <DialogDescription>
            {plan?.applied
              ? "Merge applied."
              : "Preview only — nothing has been changed yet. Review the plan below, then confirm to apply it."}
          </DialogDescription>
        </DialogHeader>

        {loading && !plan ? (
          <div className="py-8 flex justify-center">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : plan ? (
          <div className="space-y-4 text-sm">
            <div className="flex gap-4">
              <Badge variant="secondary">{plan.merged_count} to merge</Badge>
              <Badge variant="secondary">{plan.created_count} new cards</Badge>
              <Badge variant="outline">{plan.already_migrated} already migrated</Badge>
            </div>

            {plan.merged.length > 0 && (
              <div>
                <p className="font-medium mb-1.5">Will merge into existing projects</p>
                <div className="space-y-1.5">
                  {plan.merged.map(m => (
                    <div key={m.vexos_link_id} className="flex items-center justify-between bg-secondary/50 rounded-lg px-3 py-2">
                      <span>{m.vexos_display_name} <span className="text-muted-foreground">→</span> {m.portfolio_title}</span>
                      <span className="text-xs text-muted-foreground">{Math.round(m.score * 100)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {plan.created.length > 0 && (
              <div>
                <p className="font-medium mb-1.5">Will create as new published projects</p>
                <div className="space-y-1.5">
                  {plan.created.map(c => (
                    <div key={c.vexos_link_id} className="bg-secondary/50 rounded-lg px-3 py-2">
                      {c.title} {c.location && <span className="text-muted-foreground">— {c.location}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {plan.merged.length === 0 && plan.created.length === 0 && (
              <p className="text-muted-foreground py-4 text-center">Nothing left to migrate — everything is up to date.</p>
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {plan && (plan.merged.length > 0 || plan.created.length > 0) && !plan.applied && (
            <Button onClick={onConfirm} disabled={loading} data-testid="confirm-merge-btn">
              {loading ? "Applying..." : "Confirm Merge"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProjectCard({ project, isAdmin, imageUrl, vexosLink, dropboxLink, onClick, canDelete, onDelete, onCoverUploaded }) {
  const [imageError, setImageError] = useState(false);

  // A fresh cover gets a fresh chance — without this, a card whose old image
  // had failed keeps showing "No image" after a successful upload.
  useEffect(() => { setImageError(false); }, [imageUrl]);

  return (
    <div
      className="group relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer bg-zinc-900 transition-all duration-300 hover:shadow-2xl hover:shadow-black/30 hover:-translate-y-1"
      onClick={onClick}
      data-testid={`project-card-${project.id}`}
    >
      {imageUrl && !imageError ? (
        <img
          src={imageUrl}
          alt={project.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={() => setImageError(true)}
        />
      ) : (
        <div className="absolute inset-0 bg-zinc-700 flex items-center justify-center">
          <div className="text-center">
            <ImageIcon className="w-16 h-16 mx-auto text-zinc-500 mb-2" />
            <span className="text-zinc-500 text-sm">No image</span>
          </div>
        </div>
      )}

      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-80" />

      {isAdmin && project.status !== "Published" && (
        <div className="absolute top-3 left-3 z-10">
          <Badge variant={project.status === "Draft" ? "secondary" : "outline"} className="text-xs bg-black/50 backdrop-blur-sm">
            {project.status}
          </Badge>
        </div>
      )}

      {/* Quick-access link badges — labeled, not bare colored circles */}
      <div className="absolute top-3 right-3 z-10 flex gap-1.5">
        {dropboxLink && <LinkBadge href={dropboxLink} mediaType="folder_link" projectId={project.id} />}
        {vexosLink && <LinkBadge href={vexosLink} mediaType="vexos_link" projectId={project.id} />}
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
        <h3
          className="text-xl font-light text-white drop-shadow-lg"
          style={{ fontFamily: 'var(--font-sans)', textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}
        >
          {project.title}
        </h3>
        {project.location && (
          <p className="text-sm text-white/90 flex items-center gap-1.5 mt-1.5 drop-shadow-md">
            <MapPin className="w-3.5 h-3.5" />
            {project.location}
          </p>
        )}
        {/* Category only. Tags belong on the project page — three pills per card
            turned a wall of cards into noise. */}
        {project.project_type && (
          <div className="mt-2">
            <span className="text-xs bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-full text-white">
              {project.project_type}
            </span>
          </div>
        )}

        {/* What we delivered here, at a glance — the icons a rep scans for */}
        <ScopeStrip counts={project.scope_counts} />
      </div>

      {/* Hover controls: swap the cover right here (curators, incl. PMs) and
          delete (the roles that own it). Everything else lives on the page. */}
      <div className="absolute bottom-3 right-3 z-10 flex gap-1.5">
        {isAdmin && <CardCoverUpload project={project} onUploaded={onCoverUploaded} />}
        {canDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-2 rounded-full opacity-0 group-hover:opacity-100 bg-black/55 backdrop-blur-md text-white/80 hover:text-white hover:bg-black/75 transition-smooth"
            title={`Delete ${project.title}`}
            data-testid={`delete-btn-${project.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

/** The cover swap without leaving the gallery — pick a file, it uploads into
 *  Renderings and fronts the card. */
function CardCoverUpload({ project, onUploaded }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await apiClient.post(
        `/portfolio/projects/${project.id}/upload?scope=renders&set_as_hero=true`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Cover updated");
      onUploaded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't upload that image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <label
      onClick={(e) => e.stopPropagation()}
      className={`p-2 rounded-full ${uploading ? "opacity-100" : "opacity-0"} group-hover:opacity-100 bg-black/55 backdrop-blur-md text-white/80 hover:text-white hover:bg-black/75 transition-smooth cursor-pointer`}
      title={`Change the cover of ${project.title}`}
      data-testid={`cover-btn-${project.id}`}
    >
      {uploading
        ? <span className="block w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
        : <ImagePlus className="w-4 h-4" />}
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={upload}
        disabled={uploading}
        data-testid={`cover-input-${project.id}`}
      />
    </label>
  );
}

function DeleteProjectDialog({ project, deleting, onClose, onConfirm }) {
  const counts = project?.scope_counts || {};
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  // Label-then-count, like the package board — no singular/plural to get wrong.
  const breakdown = PACKAGE_SCOPES.filter(s => counts[s.key] > 0)
    .map(s => `${s.short} ${counts[s.key]}`)
    .join(" · ");

  return (
    <Dialog open={Boolean(project)} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="delete-project-dialog">
        <DialogHeader>
          <DialogTitle>Delete {project?.title?.trim()}?</DialogTitle>
          <DialogDescription>
            {total === 0
              ? "This removes the project from the portfolio. It can't be undone."
              : total === 1
                ? "This removes the project and the one item filed under it. It can't be undone."
                : `This removes the project and all ${total} items filed under it — ${breakdown}. It can't be undone.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button
            onClick={onConfirm}
            disabled={deleting}
            className="rounded-full bg-terracotta-600 text-white hover:bg-terracotta-700"
            data-testid="confirm-delete-project"
          >
            {deleting ? "Deleting…" : "Delete project"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScopeStrip({ counts }) {
  const delivered = PACKAGE_SCOPES.filter(s => (counts?.[s.key] || 0) > 0);
  if (!delivered.length) return null;
  return (
    <div className="flex items-center gap-2.5 mt-2.5 pt-2.5 border-t border-white/15">
      {delivered.map(s => (
        <span key={s.key} className="flex items-center gap-1 text-white/85" title={`${s.label} — ${counts[s.key]}`}>
          <s.Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="text-[10px] tabular">{counts[s.key]}</span>
        </span>
      ))}
    </div>
  );
}

function LinkBadge({ href, mediaType, projectId }) {
  const { label, Icon } = getLinkMeta(mediaType);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="flex items-center gap-1.5 px-2.5 py-1 bg-black/60 hover:bg-black/80 backdrop-blur-sm rounded-full text-white text-xs font-medium shadow-lg transition-all duration-200"
      title={label === "VEXOS" ? "View VEXOS experience" : "Open project files"}
      data-testid={`${getLinkMeta(mediaType).testId}-link-${projectId}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </a>
  );
}
